import { Store } from '@reduxjs/toolkit';
import { buildPageableParams, Page, PageableArgs } from '../common';
import { queryDownloadFile } from '../common/pageable/query-download-file';
import { queryUntilLastPage } from '../common/pageable/query-until-last-page';
import { servicesApi } from '../services';
import { CampagneFichierDto } from './campagne-fichier';
import { CampagneDto, CampagneProjection } from './types';
import { CampagneUpdateBodyDto } from './types/requests/campagne-update-body-dto.type';

const CAMPAGNES_URL_PREFIX = 'campagnes';

const campagneApi = servicesApi.injectEndpoints!({
  endpoints: (build) => ({

    getAllCampagnes: build.query<CampagneDto[], PageableArgs<CampagneProjection>, any>({
      queryFn: (pageable, _api, _extraOptions, baseQuery) => queryUntilLastPage({
        baseQuery,
        url: CAMPAGNES_URL_PREFIX,
        params: buildPageableParams(pageable),
      }),
      providesTags: ['Campagnes'],
    }),

    getCampagneByAnnee: build.query<CampagneDto, GetCampagneByAnneeQueryArgs, any>({
      query: ({ year, projection }) => ({
        url: `${CAMPAGNES_URL_PREFIX}/annee/${year}`,
        params: { projection },
      }),
      providesTags: ['Campagne'],
    }),

    getCampagneFichiers: build.query<Page<CampagneFichierDto>, GetCampagneFichiersQueryArgs, any>({
      query: ({ campagneId }) => ({
        url: `${CAMPAGNES_URL_PREFIX}/${campagneId}/fichiers`,
        params: { projection: CampagneProjection.LIGHT },
      }),
      providesTags: ['CampagneFichiers'],
    }),

    updateCampagne: build.mutation<void, UpdateCampagneArgs, any>({
      query: ({ id, dto }) => ({
        url: `${CAMPAGNES_URL_PREFIX}/${id}`,
        method: 'PATCH',
        body: dto,
      }),
      invalidatesTags: ['Campagne'],
    }),

    isEmailAvailable: build.query<boolean, IsEmailAvailableQueryArgs, any>({
      query: ({ email }) => `${CAMPAGNES_URL_PREFIX}/email-available/${encodeURIComponent(email)}`,
    }),

    isEmailAvailableForUpdate: build.query<boolean, IsEmailAvailableForUpdateQueryArgs, any>({
      query: ({ email, siren }) => `${CAMPAGNES_URL_PREFIX}/email-available/${email}/${siren}`,
    }),

    isEmailAdminAvailable: build.query<boolean, IsEmailAdminAvailableQueryArgs, any>({
      query: ({ organismeId, email }) => ({
        url: `${CAMPAGNES_URL_PREFIX}/email-admin-available`,
        params: { organismeId, email },
      }),
    }),

    downloadCampagneDemandes: build.mutation<void, DownloadCampagneDemandesMutationArgs, any>({
      queryFn: ({ id }, _api, _extraOptions, baseQuery) => queryDownloadFile({
        baseQuery,
        url: `${CAMPAGNES_URL_PREFIX}/${id}/export/demande`,
      }),
    }),

    createCampagne: build.mutation<CampagneDto, CreateCampagneMutationArgs, any>({
      query: ({ body }) => ({
        method: 'POST',
        url: CAMPAGNES_URL_PREFIX,
        body,
      }),
      invalidatesTags: ['Campagne'],
    }),

    getCurrentCampagne: build.query<CampagneDto, GetCurrentCampagneQueryArgs, any>({
      query: ({ projection } = {}) => ({
        url: `${CAMPAGNES_URL_PREFIX}/current`,
        params: { projection },
      }),
      providesTags: ['Campagne'],
    }),
  }),
});

interface GetCampagneByAnneeQueryArgs {
  year: number;
  projection: CampagneProjection;
}

interface GetCampagneFichiersQueryArgs {
  campagneId: number;
}

interface IsEmailAvailableQueryArgs {
  email: string;
}

interface IsEmailAvailableForUpdateQueryArgs {
  email: string;
  siren: string;
}

interface IsEmailAdminAvailableQueryArgs {
  organismeId: number;
  email: string;
}

interface DownloadCampagneDemandesMutationArgs {
  id: number;
}

export interface UpdateCampagneArgs {
  id: number;
  dto: CampagneUpdateBodyDto;
}

interface CreateCampagneMutationArgs {
  body: FormData;
}

interface GetCurrentCampagneQueryArgs {
  projection: CampagneProjection;
}

export const {
  useGetAllCampagnesQuery,
  useGetCampagneByAnneeQuery,
  useGetCampagneFichiersQuery,
  useUpdateCampagneMutation,
  useLazyIsEmailAvailableQuery,
  useLazyIsEmailAdminAvailableQuery,
  useDownloadCampagneDemandesMutation,
  useCreateCampagneMutation,
  useGetCurrentCampagneQuery,
} = campagneApi;

export function checkIfEmailIsCampagneAvailable(store: Store, args: IsEmailAvailableQueryArgs): Promise<any> {
  return store.dispatch(campagneApi.endpoints.isEmailAvailable.initiate(args, { forceRefetch: true })); // On force le refetch car un email peut-être "libéré" en parallèle
}

export function checkIfEmailIsCampagneAvailableForUpdate(store: Store, args: IsEmailAvailableForUpdateQueryArgs): Promise<any> {
  return store.dispatch(campagneApi.endpoints.isEmailAvailableForUpdate.initiate(args, { forceRefetch: true })); // On force le refetch car un email peut-être "libéré" en parallèle
}
